import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { scrollToSection } from "../lib/scroll";

const MENU = [
  { id: "#projetos", key: "projetos" },
  { id: "#abordagem", key: "abordagem" },
  { id: "#sobre-nos", key: "sobre" },
  { id: "#contactos", key: "contactos" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      scrollToSection(id);
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled || open
          ? "border-white/10 bg-basalt/85 backdrop-blur-md"
          : "border-transparent bg-gradient-to-b from-basalt/80 via-basalt/40 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-6">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          aria-label="AZORUniqueHome — início"
          className="transition-opacity hover:opacity-80"
        >
          <img src="/logo.svg" alt="AZORUniqueHome" className="h-6 w-auto" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-menu">
          {MENU.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-link-${item.key}`}
              onClick={() => go(item.id)}
              className="group relative font-mono text-[11px] tracking-[0.25em] text-sand/70 transition-colors hover:text-sand"
            >
              {t.nav[item.key]}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <div className="flex items-center rounded-full border border-white/15 p-0.5" data-testid="language-toggle">
            {["pt", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`rounded-full px-3 py-1 font-mono text-[11px] tracking-widest transition-colors ${
                  lang === l ? "bg-terracotta text-basalt" : "text-sand/60 hover:text-sand"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="flex items-center gap-1.5 font-mono text-[11px] tracking-widest text-sand/40 transition-colors hover:text-terracotta"
          >
            <Lock size={12} />
            {t.nav.admin}
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
          onClick={() => setOpen((v) => !v)}
          className="text-sand lg:hidden"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-basalt/95 backdrop-blur-md lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {MENU.map((item) => (
                <button
                  key={item.key}
                  data-testid={`nav-mobile-link-${item.key}`}
                  onClick={() => go(item.id)}
                  className="py-3 text-left font-mono text-xs tracking-[0.25em] text-sand/80 transition-colors hover:text-terracotta"
                >
                  {t.nav[item.key]}
                </button>
              ))}
              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                <div className="flex items-center rounded-full border border-white/15 p-0.5">
                  {["pt", "en"].map((l) => (
                    <button
                      key={l}
                      data-testid={`lang-toggle-mobile-${l}`}
                      onClick={() => setLang(l)}
                      className={`rounded-full px-3 py-1 font-mono text-[11px] tracking-widest transition-colors ${
                        lang === l ? "bg-terracotta text-basalt" : "text-sand/60"
                      }`}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
                <Link
                  to="/admin"
                  data-testid="nav-mobile-admin-link"
                  className="flex items-center gap-1.5 font-mono text-[11px] tracking-widest text-sand/50"
                >
                  <Lock size={12} />
                  {t.nav.admin}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
